// Third party
import Box from '@mui/material/Box';
import {
  Outlet, useLocation, Link as RouterLink, useNavigate,
} from 'react-router-dom';
import './main.css';
import {
  useMemo, useRef,
} from 'react';
import { NavVertical, Header, NavHorizontal } from '@mgm/common/layout/main';
import { useTranslation } from 'react-i18next';
import { useAvatarFile } from '@mgm/common/hooks/usePersonnel';
import { isMobileDevice } from '@mgm/common/utils';
import { useResponsive } from '@mgm/common/hooks/useResponsive';
import { SendFeedback } from '../../features/help/components';
import { JoinDiscord } from '../../components/JoinDiscord';
import { ReleaseNav } from '../../components/ReleaseNav';
import { PricingDialog } from '../../components/PricingDialog';
import { ShutdownBanner } from '../../components/ShutdownBanner';
import Main from './Main';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { getIsDrawerOpen } from '../../store/common/selectors';
import { commonActions } from '../../store/common/slice';
import navConfig from '../navConfig';
import { getAuthenticateUser, getTokenDetails } from '../../store/authenticate/selectors';
import { useCommon } from '../../hooks/useCommon';
import { NavItem } from './Drawer/NavItem';
import { Clock } from '../../features/clock';
import { ContactsPopover } from './Bar/ContactsPopover';
import { version } from '../../../package.json';
import { getIsBreakStarted, getIsShiftStarted } from '../../store/clock/selectors';
import { ROUTES } from '../../data';
import { helpActions } from '../../store/help/slice';
import NavList from './Drawer/NavList';

const MainLayout = () => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const menuIconRef = useRef<HTMLButtonElement>(null);
  const isOpenDrawer = useAppSelector(getIsDrawerOpen);
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { permissions } = useAppSelector(getTokenDetails);
  const isShiftStarted = useAppSelector(getIsShiftStarted);
  const isBreakStarted = useAppSelector(getIsBreakStarted);
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'md');

  const { handleLogout } = useCommon();

  const { avatarSrc } = useAvatarFile(personnel?.avatarFile || '');

  const { pathname } = useLocation();

  const onLogout = async () => {
    handleLogout();
  };

  const onCloseNav = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  const onToggleLayout = () => {
    dispatch(commonActions.setToggleDrawer(!isOpenDrawer));
  };

  const onReleaseIconClicked = () => {
    dispatch(commonActions.setIsOpenReleaseNav(true));
  };

  const menuItems = [
    {
      name: t('settings'),
      value: 'settings',
      action: () => {
        navigate(ROUTES.SETTINGS);
      },
      isSelected: false,
    },
    {
      name: t('sendFeedback', { ns: 'help' }),
      value: 'feedback',
      action: () => {
        setTimeout(() => {
          dispatch(helpActions.setIsOpenFeedbackNav(true));
        }, 300);
      },
      isSelected: false,
    },
    {
      name: 'ManageMate community',
      value: 'discord',
      action: () => {
        setTimeout(() => {
          dispatch(helpActions.setIsOpenDiscordNav(true));
        }, 300);
      },
      isSelected: false,
    },
  ];

  const isAllowedClockIn = useMemo(() => (isMobileDevice ? permissions.clockIn?.mobile : permissions.clockIn?.web), [permissions, isMobileDevice]);

  const isNavHorizontal = window !== window.parent;

  const renderMainHeader = (
    <Header
      ClockComponent={Clock}
      ContactsPopover={ContactsPopover}
      menuIconRef={menuIconRef}
      isOpenDrawer={isOpenDrawer}
      handleDrawerToggle={onToggleLayout}
      personnelDetails={{
        personnelName: personnel?.name,
        personnelAvatarFile: personnel?.avatarFile,
        personnelEmail: personnel?.email,
      }}
      isAllowedClockIn={isAllowedClockIn}
      onReleaseIconClicked={onReleaseIconClicked}
      clockState={{
        isShiftStarted,
        isBreakStarted,
      }}
      handleLogout={onLogout}
      appVersion={version}
      logoutButtonText={t('logout')}
      menuItems={menuItems}
    />
  );

  const renderNavVertical = (
    <NavVertical
      isOpenDrawer={isOpenDrawer}
      menuIconRef={menuIconRef}
      onCloseNav={onCloseNav}
      navConfig={navConfig}
      onToggleLayout={onToggleLayout}
      personnelData={{
        personnelName: personnel?.name || '',
        personnelAvatarFile: avatarSrc,
        permissionLevel: personnel?.permissionLevel || '',
      }}
      NavItemComponent={NavItem}
      routePathName={pathname}
      LinkComponent={RouterLink}
    />
  );

  if (isNavHorizontal) {
    return (
      <>
        {renderMainHeader}

        {isDesktop ? (
          <NavHorizontal
            navConfig={navConfig}
            NavListComponent={NavList}
          />
        ) : renderNavVertical}

        <Main>
          <ShutdownBanner />
          <Outlet />
        </Main>
      </>
    );
  }

  return (
    <>
      {renderMainHeader}
      <Box
        sx={{
          display: { lg: 'flex' },
          minHeight: { lg: 1 },
        }}
      >
        {renderNavVertical}

        <Main>
          <ShutdownBanner />
          <Outlet />
          <SendFeedback />
          <JoinDiscord />
          <ReleaseNav />
          <PricingDialog />
        </Main>
      </Box>
    </>
  );
};

export {
  MainLayout,
};
